import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image } from "@react-pdf/renderer"
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
    getAdditionalGroups,
    getAllAdditionalItems,
    computeQuotationTotalAmount,
    computeStandaloneItemsTotal,
} from "../../_utils/quotationTotals";
import NotoSansJPRegular from "./fonts/NotoSansJP-Regular.ttf"
import NotoSansJPBold from "./fonts/NotoSansJP-Bold.ttf"
import NotoSerifJPRegular from "./fonts/NotoSerifJP-Regular.ttf"
import NotoSerifJPBold from "./fonts/NotoSerifJP-Bold.ttf"


const tlf_logo_quotation = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/tlf-logo-quotation.png";
Font.register({
    family: "Noto Sans JP",
    fonts: [
        {
            src: NotoSansJPRegular,
            fontWeight: 400,
        },
        {
            src: NotoSansJPBold,
            fontWeight: 700,
        },
    ]
})

Font.register({
    family: "Noto Serif JP",
    fonts: [
        {
            src: NotoSerifJPRegular,
            fontWeight: 400,
        },
        {
            src: NotoSerifJPBold,
            fontWeight: 700,
        },
    ]
})

// Create styles
const styles = StyleSheet.create({

    page: {
        padding: 30,
        fontFamily: "Noto Sans JP",
        fontSize: "12px",
        color: "#333",
    },
    // NOTE: direct children of <Page> must not use `marginBottom`. react-pdf's
    // pagination counts a node's bottom margin as part of its "end of presence":
    // a section that itself fits on the page but whose bottom margin spills past
    // the wrap area is pushed to a fresh page, where it lays out identically and
    // is pushed again — an infinite loop that freezes the whole tab. Spacing
    // between page-level sections is therefore expressed as `marginTop` on the
    // following section instead.
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingBottom: 20,
        borderBottomWidth: 1,
        borderBottomColor: "#eee",
    },
    headerTitle: {
        fontSize: "28px",
        fontFamily: "Noto Serif JP",
        fontWeight: 700,
        color: "#212529"
    },
    logo: {
        width: 50,
        height: 50,
        justifyContent: "center",
        alignItems: "center",
    },
    infoSection: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 20,
        paddingBottom: 10,
    },
    clientInfo: {
        flex: 2,
        marginRight: 5,
    },
    companyInfo: {
        flex: 2,
        marginRight: 10,
    },
    quoteInfo: {
        flex: 1.5,
        alignItems: "flex-end",
    },
    quotationNumberLabel: {
        color: "#777",
        fontSize: "8px",
        marginBottom: 5,
    },
    quotationNumberValue: {
        fontWeight: "bold",
        textAlign: "right",
        fontSize: "8px",
    },
    quoteInfoTbl: {
        width: "50%",
    },
    quoteInfoTitle: {
        paddingHorizontal: 10,
        textAlign: "center",
        fontSize: "10px",
    },
    quoteInfoValue: {
        paddingHorizontal: 10,
        fontSize: "12px",
        textAlign: "center",
        backgroundColor: "white",
    },
    quoteInfoTable: {
        width: "100%",
        border: 0,
        fontFamily: "Noto Sans JP",
        fontSize: "10px",
    },
    tdLabel: {
        color: "#777",
        fontSize: "10px",
        lineHeight: 1.5,
        marginBottom: 7,
        width: "1%"
    },
    tdContent: {
        fontSize: "10px",
        lineHeight: 1.5,
        marginBottom: 7,
    },
    infoLabel: {
        color: "#A7A7A7",
        fontSize: "8px",
        marginBottom: 5,
        textTransform: "uppercase",
    },
    infoContent: {
        fontSize: "10px",
        lineHeight: 1.5,
        paddingBottom: 10,
        color: "#4E4E4E"
    },
    infoName: {
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "uppercase",
        paddingBottom: 5,
        color: "#4E4E4E"
    },
    infoEmail: {
        color: "#BF993A", // Gold color for email links
        fontSize: "10px",
        paddingBottom: 10,
        textDecoration: "underline"
    },
    infoRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 10,
    },
    infoValue: {
        fontWeight: "bold",
        textAlign: "right",
    },
    totalSection: {
        flexDirection: "row",
        marginTop: 30,
        borderWidth: 1,
        borderColor: "#ddd",
        width: 200,
    },
    validUntil: {
        width: "50%",
        padding: 10,
        borderRightWidth: 1,
        borderRightColor: "#ddd",
        verticalAlign: 'center',
    },
    validLabel: {
        fontSize: "10px",
        textTransform: "uppercase",
        // marginBottom: 5,
        textAlign: "center",
        paddingTop: 10,
        color: "#666666"
    },
    validDate: {
        fontSize: "10px",
        fontWeight: "bold",
        textAlign: "center",
        color: "#282828"
    },
    totalAmount: {
        width: "50%",
    },
    totalLabel: {
        backgroundColor: "#282828",
        color: "white",
        padding: 10,
        textAlign: "center",
        fontSize: "10px",
        fontWeight: "bold",
        textTransform: "uppercase",
    },
    totalValue: {
        padding: 10,
        fontSize: "12px",
        fontWeight: "bold",
        textAlign: "center",
        backgroundColor: "white",
        color: "#282828"
    },
    servicesSection: {
        marginTop: 30,
    },
    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#282828",
        color: "white",
        padding: 10,
        fontWeight: "bold",
    },
    nameDescCol: {
        width: "35%",
    },
    nameCol: {
        fontSize: "11px",
        fontWeight: "bold",
    },
    descCol: {
        fontSize: "10px",
        fontWeight: 400
    },
    dateCol: {
        padding: 10,
        width: "22%",
        fontSize: "10px",
        fontWeight: "bold",
    },
    priceCol: {
        padding: 10,
        width: "18%",
        textAlign: "right",
        fontSize: "10px",
        fontWeight: "bold",
        flexShrink: 0,
    },
    qtyCol: {
        padding: 10,
        width: "5%",
        textAlign: "center",
        fontSize: "10px",
        fontWeight: "bold",
    },
    amountCol: {
        padding: 10,
        width: "20%",
        textAlign: "right",
        fontSize: "10px",
        fontWeight: "bold",
        flexShrink: 0,
    },
    nameDescColVal: {
        width: "35%",
        paddingVertical: 10,
        paddingRight: 15,
    },
    nameColVal: {
        fontSize: "11px",
        fontWeight: "bold",
    },
    descColVal: {
        fontSize: "10px",
        fontWeight: 400
    },
    dateColVal: {
        paddingVertical: 10,
        paddingRight: 20,
        width: "22%",
        fontSize: "10px",
        height: "100%"
    },
    dateColValDouble: {
        paddingVertical: 10,
        paddingRight: 37,
        paddingLeft: 2,
        width: "22%",
        fontSize: "10px",
        height: "100%"
    },
    priceColVal: {
        paddingVertical: 10,
        paddingHorizontal: 10,
        width: "18%",
        textAlign: "right",
        fontSize: "10px",
        flexShrink: 0,
    },
    qtyColVal: {
        paddingVertical: 10,
        paddingHorizontal: 10,
        width: "5%",
        textAlign: "center",
        fontSize: "10px",
    },
    amountColVal: {
        paddingVertical: 10,
        paddingHorizontal: 10,
        width: "20%",
        textAlign: "right",
        fontSize: "10px",
        fontWeight: "bold",
        flexShrink: 0,
    },
    subheader: {
        fontSize: " 8px",
        fontWeight: "normal",
        color: "#aaa",
    },
    categoryRow: {
        flexDirection: "row",
        backgroundColor: "#7C7B7B",
        padding: 10,
        fontWeight: 400,
        fontSize: "10px",
        color: "#FFFFFF"
    },
    tableRow: {
        flexDirection: "row",
        // borderBottomWidth: 1,
        // borderBottomColor: "#DADADA",
        borderTopWidth: 1,
        borderTopColor: "#DADADA",
        padding: 10,
        color: "#4E4E4E"
    },
    tableRowWithNoBorderBottom: {
        flexDirection: "row",
        // borderBottomWidth: 1,
        // borderBottomColor: "#eee",
        padding: 10,
        color: "#4E4E4E"
    },
    description: {
        fontSize: "9px",
        color: "#666",
    },
    subtotalRow: {
        flexDirection: "row",
        backgroundColor: "#DCDDDE",
        paddingVertical: 10,
        paddingHorizontal: 10,
        fontWeight: "bold",
        fontSize: "11px",
        color: "#4E4E4E",
        textAlign: "center",
        alignItems: "center" // Add this line to vertically center the children
    },
    subtotalLabel: {
        fontSize: "9px",
        width: "80%",
        textAlign: "right",
    },
    subtotalValue: {
        width: "20%",
        textAlign: "right",
        flexShrink: 0,
    },
    discountRowWithBorderBottom: {
        flexDirection: "row",
        backgroundColor: "#F5F5F5",
        fontFamily: 'Noto Sans JP',
        padding: 10,
        // fontWeight: "bold",
        fontSize: "9px",
        color: "#282828",
        borderBottomWidth: 1,
        borderBottomColor: "#DADADA",
    },
    discountRow: {
        flexDirection: "row",
        backgroundColor: "#F5F5F5",
        fontFamily: 'Noto Sans JP',
        padding: 10,
        // fontWeight: "bold",
        fontSize: "9px",
        color: "#282828",
    },
    discountLabel: {
        width: "80%",
        textAlign: "right",
    },
    discountValue: {
        width: "15%",
        textAlign: "left",
        flexShrink: 0,
    },
    discountValueEmpty: {
        width: "5%",
        textAlign: "left",
    },
    transferNotesRow: {
        flexDirection: "row",
        backgroundColor: "#F5F5F5",
        fontFamily: 'Noto Sans JP',
        padding: 10,
        // fontWeight: "bold",
        fontSize: "9px",
        color: "#282828",
    },
    transferNotesLabel: {
        width: "22%",
        textAlign: "left",
        fontWeight: "bold",
    },
    transferNotesValue: {
        width: "78%",
        textAlign: "left",
    },
    subtotalRowDup: {
        width: "100%",
        flexDirection: "row",
        // backgroundColor: "#eee",
        // padding: 10,
        // fontWeight: "bold",
        fontSize: "11px",
        // width: 155,
        // alignItems: "flex-end",
    },
    subtotalLabelDup: {
        width: "50%",
        textAlign: "left",
        color: "#7C7B7B",
        fontSize: "10px",
        marginBottom: 7,
    },
    subtotalValueDup: {
        width: "50%",
        textAlign: "left",
        fontSize: "10px",
        lineHeight: 1.5,
        marginBottom: 7,
        color: "#4E4E4E"
    },
    totalRow: {
        flexDirection: "row",
        backgroundColor: "#333333",
        color: "white",
        padding: 10,
        fontWeight: "bold",
        fontSize: "11px",
    },
    totalRowLabel: {
        width: "80%",
        textAlign: "right",
    },
    totalRowLabel1: {
        fontWeight: "bold",
        fontSize: "10px",
        width: "80%",
        textAlign: "right",
    },
    totalRowLabel2: {
        fontSize: "8px",
        width: "80%",
        textAlign: "right",
    },
    totalRowValue: {
        width: "20%",
        textAlign: "right",
        flexShrink: 0,
    },
    notesSection: {
        marginTop: 15,
        paddingTop: 10,
    },
    notesTitle: {
        fontSize: "12px",
        marginBottom: 5,
        fontWeight: "bold",
        fontFamily: "Noto Serif JP",
        color: "#282828"
    },
    quotationNotesTitle: {
        fontSize: "12px",
        marginBottom: 5,
        fontWeight: "bold",
        fontFamily: "Noto Serif JP",
        color: "#282828"
    },
    note: {
        marginBottom: 10,
        fontFamily: "Noto Sans JP",
        color: "#282828"
    },
    noteTitle: {
        fontSize: "12px",
        // marginBottom: 5,
        fontWeight: "bold",
        fontSize: "10px",
    },
    noteContent: {
        fontSize: "10px",
        color: "#666",
        fontSize: "10px",
        // marginBottom: 5,
    },
    bookingItemsVehicleDetailsRow: {
        flexDirection: "row",
        paddingHorizontal: 10,
        paddingVertical: 0,
        fontFamily: 'Noto Sans JP',
        fontSize: "9px",
        borderTop: "0.5px dashed #DADADA !important",
    },
    bookingItemsVehicleDetailsRowWithBorderBottom: {
        flexDirection: "row",
        paddingHorizontal: 10,
        paddingVertical: 0,
        fontFamily: 'Noto Sans JP',
        fontSize: "9px",
        borderTop: "0.5px dashed #DADADA !important",
        borderBottomWidth: 1,
        borderBottomColor: "#DADADA",
    },
    bookingItemsVehicleDetailsValueEmpty: {
        width: "30%",
        textAlign: "right",
    },
    bookingItemsVehicleDetailsLabel: {
        width: "50%",
        textAlign: "left",
        alignSelf: "center",
    },
    bookingItemsVehicleDetailsValue: {
        width: "20%",
        textAlign: "right",
        paddingVertical: 10,
        paddingHorizontal: 10,
        flexShrink: 0,
    },
    textDescription: {
        fontFamily: 'Noto Sans JP',
        fontWeight: "400",
        fontSize: "13px",
    },
    borderTopDashed: {
        borderTop: "1px dashed #DADADA !important",
        borderBottom: "1px dashed #DADADA !important",
    },
    additionalItemsHeaderRow: {
        flexDirection: "row",
        backgroundColor: "#F1F1F1",
        paddingHorizontal: 10,
        paddingVertical: 8,
        fontSize: "9px",
        color: "#212529",
        borderTopWidth: 1,
        borderTopColor: "#DADADA",
    },
    additionalItemsHeaderLabel: {
        flex: 1,
        textAlign: "left",
    },
    tableRowDashed: {
        flexDirection: "row",
        borderTop: "0.5px dashed #DADADA",
        padding: 10,
        color: "#4E4E4E",
    },
    additionalItemsDataRow: {
        flexDirection: "row",
        borderTopWidth: 1,
        borderTopColor: "#DADADA",
        paddingBottom: 0,
        paddingTop: 0,
        paddingHorizontal: 10,
        color: "#4E4E4E",
    },
    additionalItemsDataRowDashed: {
        flexDirection: "row",
        borderTop: "0.5px dashed #DADADA",
        paddingBottom: 0,
        paddingTop: 0,
        paddingHorizontal: 10,
        color: "#4E4E4E",
    }
})

const formatMomentDate = {
    "EN": "MMMM D, YYYY",
    "en": "MMMM D, YYYY",
    "JP": "YYYY年M月D日",
    "jp": "YYYY年M月D日",
}

const getPhoneNumber = (item) => {
    if (item.role === "individual" || item.role === "business") {
        const accountDetail = item[item.role === "individual" ? "individualDetail" : "businessDetail"]
        return accountDetail.billingInformation.phone !== "" ? `(${accountDetail.billingInformation.prefix_number ?? "-"}) ${accountDetail.billingInformation.phone}` : "-";
    }
    return "";
};

const getBookingType = (bookingType, isAirportTransfer) => {
    switch (bookingType) {
        case "byTheHour":
            return "byTheHour"
        case "specifiedDestination":
            if (isAirportTransfer) {
                return "airportTransfer"
            } else {
                return "specifiedDestination"
            }
        default:
            return "";
    }
}

const bookingItemsDetailOrder = [
    "charterCost",
    "lateNightFee",
    "discount",
    "tollFee",
    "longDistanceSurcharge",
    "driverAccomodationTotal"
]

// Every amount printed in the PDF goes through this. A missing amount used to
// throw (`undefined.toLocaleString()`), and because @react-pdf renders inside
// <PDFDownloadLink> with no error boundary above it, that single undefined took
// down the whole download page instead of one row.
const formatAmount = (amount) => Number(amount || 0).toLocaleString();

// Create Document Component
const QuotationDocument = ({ data }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || localStorage.getItem("lang") || "EN";

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <Text style={styles.headerTitle}>{t("Quotation.LinkPage.Quotation")}</Text>
                    <View style={styles.logo}>
                        <Image
                            style={styles.image}
                            src={tlf_logo_quotation}
                        />
                    </View>
                </View>

                {/* Info Section */}
                <View style={styles.infoSection}>
                    <View style={styles.clientInfo}>
                        <Text style={styles.infoLabel}>{t("Quotation.LinkPage.To.English")}</Text>
                        <Text style={styles.infoName}>{data?.user?.name} {" "} {t("Quotation.LinkPage.To.Japanese")}</Text>
                        <Text style={styles.infoEmail}>{data?.user?.email}</Text>
                        <Text style={styles.infoContent}>{data?.user?.role === "individual" ? data?.user?.individualDetail?.billingInformation?.address : data?.user?.businessDetail?.billingInformation?.address}</Text>
                        <Text style={styles.infoContent}>{data?.user && getPhoneNumber(data?.user)}</Text>
                    </View>
                    <View style={styles.companyInfo}>
                        <Text style={styles.infoLabel}>{t("Quotation.LinkPage.From")}</Text>
                        <Text style={styles.infoName}>{t("Quotation.LinkPage.CompanyDetail.Name")}</Text>
                        <Text style={styles.infoEmail}>{t("Quotation.LinkPage.CompanyDetail.Email")}</Text>
                        <Text style={styles.infoContent}>{t("Quotation.LinkPage.CompanyDetail.Address")}</Text>
                        <Text style={styles.infoContent}>{t("Quotation.LinkPage.CompanyDetail.Telp")}</Text>
                    </View>
                    <View style={styles.quoteInfo}>
                        <View style={styles.subtotalRowDup}>
                            <Text style={styles.subtotalLabelDup}>{t("Quotation.LinkPage.IssueDate")}</Text>
                            <Text style={styles.subtotalValueDup}>{data && moment(data?.issueDate).format(formatMomentDate[lang])}</Text>
                        </View>
                        <View style={styles.subtotalRowDup}>
                            <Text style={styles.subtotalLabelDup}>{t("Quotation.LinkPage.QuotationNumber")}</Text>
                            <Text style={styles.subtotalValueDup}>{data?.quotationID}</Text>
                        </View>
                    </View>
                </View>

                {/* Total Section */}
                <View style={styles.totalSection}>
                    <View style={styles.validUntil}>
                        <Text style={styles.validLabel}>{t("Quotation.LinkPage.ValidUntil")}</Text>
                        <Text style={styles.validDate}>{data && moment(data?.expiredDate).format(formatMomentDate[lang])}</Text>
                    </View>
                    <View style={styles.totalAmount}>
                        <Text style={styles.totalLabel}>{t("Quotation.LinkPage.Total")}</Text>
                        <Text style={styles.totalValue}>
                            {`${formatAmount(computeQuotationTotalAmount(data))}${t("Quotation.LinkPage.JPY")}`}
                        </Text>
                    </View>
                </View>

                <View style={styles.servicesSection}>
                    <View style={styles.tableHeader}>
                        <Text style={styles.dateCol}>{t("Quotation.LinkPage.Table.ServiceDate")}</Text>
                        <View style={styles.nameDescCol}>
                            <Text style={styles.nameCol}>{t("Quotation.LinkPage.Table.Name")}</Text>
                            <Text style={styles.descCol}>{t("Quotation.LinkPage.Table.Description")}</Text>
                        </View>
                        <Text style={styles.priceCol}>{t("Quotation.LinkPage.Table.UnitPrice")}</Text>
                        <Text style={styles.qtyCol}>{t("Quotation.LinkPage.Table.Qty")}</Text>
                        <Text style={styles.amountCol}>{t("Quotation.LinkPage.Table.Amount")}</Text>
                    </View>

                    {/* Table Content */}
                    {data?.bookings.map((item, index) => (
                        <View key={index}>
                            {/* Category Row — keep with its first item by using wrap={false} */}
                            <View style={styles.categoryRow} wrap={false}>
                                <Text style={{ flex: 1 }}>
                                    {t(`BookingType.${getBookingType(item?.bookingType, item?.isAirportTransfer)}`)}
                                </Text>
                            </View>

                            {/* Service Items — each item + its breakdown sub-rows grouped together */}
                            {item?.bookingItems.map((b, bIndex) => (
                                <View key={bIndex} wrap={false}>
                                    <View style={styles.tableRow}>
                                        {
                                            b?.serviceDate?.split("-").length > 1 ? (
                                                <Text style={styles.dateColValDouble}>
                                                    {
                                                        lang?.toUpperCase() === "JP" ? b?.serviceDateJP || b?.serviceDate : b?.serviceDate
                                                    }
                                                </Text>
                                            ) : (
                                                <Text style={styles.dateColVal}>
                                                    {
                                                        lang?.toUpperCase() === "JP" ? b?.serviceDateJP || b?.serviceDate : b?.serviceDate
                                                    }
                                                </Text>
                                            )
                                        }
                                        <View style={styles.nameDescColVal}>
                                            <Text style={styles.nameColVal}>
                                                {
                                                    lang?.toUpperCase() === "JP" ? b?.nameJP || b?.name : b?.name
                                                }
                                            </Text>
                                            {
                                                (b.desc) && (
                                                    <Text style={styles.descColVal}>
                                                        {
                                                            lang?.toUpperCase() === "JP" ? b?.descJP || b?.desc : b?.desc
                                                        }
                                                    </Text>
                                                )
                                            }
                                        </View>
                                        <Text style={styles.priceColVal}>{`${formatAmount(b?.price)}${t("Quotation.LinkPage.JPY")}`}</Text>
                                        <Text style={styles.qtyColVal}>{b.qty}</Text>
                                        <Text style={styles.amountColVal}>{`${formatAmount((b?.price || 0) * (b?.qty || 0))}${t("Quotation.LinkPage.JPY")}`}</Text>
                                    </View>
                                    {
                                        (b.type === "vehicle" && item?.dataVersion === "v3") && (
                                            bookingItemsDetailOrder.map((detailId, detailIndex) => {
                                                // "discount" is not a `details` entry: it is rendered from the
                                                // booking item's own discount fields, and only when this transfer
                                                // is discounted.
                                                if (detailId === "discount") {
                                                    if (!item?.useDiscount) return null;

                                                    return (
                                                        <View key={detailId} style={styles.discountRow}>
                                                            <Text style={styles.discountLabel}>{`${t("Quotation.LinkPage.Table.Discount")} (${Number(b?.discount || 0)}%)`}</Text>
                                                            <Text style={styles.discountValueEmpty}></Text>
                                                            <Text style={styles.discountValue}>
                                                                {b?.totalDiscount
                                                                    ? `-${formatAmount(b?.totalDiscount)}${t("Quotation.LinkPage.JPY")}`
                                                                    : `0${t("Quotation.LinkPage.JPY")}`}
                                                            </Text>
                                                        </View>
                                                    );
                                                }

                                                // Look the breakdown row up once. An older record can be missing a
                                                // row entirely, so a not-found lookup skips the row instead of
                                                // reading `.amount` off undefined.
                                                const detail = b?.details?.find((entry) => entry?.id === detailId);
                                                if (!(detail?.amount > 0)) return null;

                                                const hasLaterNonZeroDetail = b?.details?.slice(detailIndex + 1).some((entry) => entry?.amount > 0);

                                                return (
                                                    <View
                                                        key={detailId}
                                                        style={(!hasLaterNonZeroDetail && !item?.useDiscount) ? styles.bookingItemsVehicleDetailsRowWithBorderBottom : styles.bookingItemsVehicleDetailsRow}
                                                    >
                                                        <Text style={styles.bookingItemsVehicleDetailsValueEmpty}></Text>
                                                        <Text style={styles.bookingItemsVehicleDetailsLabel}>
                                                            {detail.id === "lateNightFee"
                                                                ? t(`Quotation.LinkPage.Table.${detail.id}`, { hour: detail.nightHours || 0 })
                                                                : detail.id
                                                                    ? t(`Quotation.LinkPage.Table.${detail.id}`)
                                                                    : detail.name}
                                                        </Text>
                                                        <Text style={styles.bookingItemsVehicleDetailsValue}>{`${formatAmount(detail.amount)}${t("Quotation.LinkPage.JPY")}`}</Text>
                                                    </View>
                                                );
                                            })
                                        )
                                    }
                                </View>
                            ))}

                            {/* Additional Items */}
                            {getAdditionalGroups(item).map((group, groupIndex) => (
                                <React.Fragment key={groupIndex}>
                                    {group.heading ? (
                                        <View style={styles.additionalItemsHeaderRow} wrap={false}>
                                            <Text style={styles.additionalItemsHeaderLabel}>{group.heading}</Text>
                                        </View>
                                    ) : null}
                                    {(group.items || []).map((additionalItem, additionalIndex) => {
                                        const itemName = additionalItem?.name || "-";
                                        const additionalItemServiceDate = additionalItem?.serviceDate
                                            ? moment(additionalItem.serviceDate).format(formatMomentDate[lang] || formatMomentDate["EN"])
                                            : "";
                                        const isFirstRow = !group.heading && additionalIndex === 0;
                                        return (
                                            <View key={additionalIndex} style={isFirstRow ? styles.additionalItemsDataRow : styles.additionalItemsDataRowDashed} wrap={false}>
                                                <Text style={styles.dateColVal}>{additionalItemServiceDate}</Text>
                                                <View style={styles.nameDescColVal}>
                                                    <Text style={styles.descColVal}>{itemName}</Text>
                                                </View>
                                                <Text style={styles.priceColVal}>{`${Number(additionalItem?.price || 0).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</Text>
                                                <Text style={styles.qtyColVal}>{additionalItem?.qty != null ? String(additionalItem.qty) : ""}</Text>
                                                <Text style={styles.amountColVal}>{`${Number(additionalItem?.totalPrice || 0).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</Text>
                                            </View>
                                        );
                                    })}
                                </React.Fragment>
                            ))}

                            {/* Subtotal Row */}
                            <View style={styles.subtotalRow} wrap={false}>
                                <Text style={styles.subtotalLabel}>{t("Quotation.LinkPage.Table.Subtotal")}</Text>
                                <Text style={styles.subtotalValue}>
                                    {`${(
                                        (item?.bookingItems || []).reduce(
                                            (subtotal, b) => subtotal + (b.discountedPrice ? b.discountedPrice : b.totalPrice),
                                            0
                                        ) +
                                        getAllAdditionalItems(item).reduce(
                                            (subtotal, a) => subtotal + Number(a?.totalPrice || 0),
                                            0
                                        )
                                    ).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}
                                </Text>
                            </View>

                            {
                                (item?.useDiscount && item?.dataVersion === "v2") && (
                                    <View style={styles.discountRow} wrap={false}>
                                        <Text style={styles.discountLabel}>{`DISCOUNT ${Number(item?.discount || 0)}%`}</Text>
                                        <Text style={styles.discountValue}>
                                            {`-${formatAmount(item?.totalDiscount)}${t("Quotation.LinkPage.JPY")}`}
                                        </Text>
                                    </View>
                                )
                            }

                            {
                                item?.message?.length > 0 && (
                                    <View style={styles.transferNotesRow} wrap={false}>
                                        <Text style={styles.transferNotesLabel}>{`${t("Quotation.LinkPage.TransferNotes")}`}</Text>
                                        {item?.message ? (
                                            item.message.map((m, noteIndex) => <Text key={noteIndex} style={styles.transferNotesValue}>{m.note}</Text>)
                                        ) : (
                                            <Text style={styles.transferNotesValue}>-</Text>
                                        )}
                                    </View>
                                )
                            }
                        </View>
                    ))}

                    {/* Standalone Additional Items */}
                    {(data?.standaloneItemsGroups?.length > 0) && (
                        <View>
                            <View style={styles.categoryRow} wrap={false}>
                                <Text style={{ flex: 1 }}>{t("Quotation.LinkPage.AdditionalItems") || "Additional Items"}</Text>
                            </View>
                            {data.standaloneItemsGroups.map((group, groupIndex) => (
                                <React.Fragment key={groupIndex}>
                                    {group.heading ? (
                                        <View style={styles.additionalItemsHeaderRow} wrap={false}>
                                            <Text style={styles.additionalItemsHeaderLabel}>{group.heading}</Text>
                                        </View>
                                    ) : null}
                                    {(group.items || []).map((item, itemIndex) => {
                                        const isFirstRow = !group.heading && itemIndex === 0;
                                        const serviceDate = item?.serviceDate
                                            ? moment(item.serviceDate).format(formatMomentDate[lang] || formatMomentDate["EN"])
                                            : "";
                                        return (
                                            <View key={itemIndex} style={isFirstRow ? styles.additionalItemsDataRow : styles.additionalItemsDataRowDashed} wrap={false}>
                                                <Text style={styles.dateColVal}>{serviceDate}</Text>
                                                <View style={styles.nameDescColVal}>
                                                    <Text style={styles.descColVal}>{item?.name || "-"}</Text>
                                                </View>
                                                <Text style={styles.priceColVal}>{`${Number(item?.price || 0).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</Text>
                                                <Text style={styles.qtyColVal}>{item?.qty != null ? String(item.qty) : ""}</Text>
                                                <Text style={styles.amountColVal}>{`${Number(item?.totalPrice || 0).toLocaleString()}${t("Quotation.LinkPage.JPY")}`}</Text>
                                            </View>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                            <View style={styles.subtotalRow} wrap={false}>
                                <Text style={styles.subtotalLabel}>{t("Quotation.LinkPage.Table.Subtotal")}</Text>
                                <Text style={styles.subtotalValue}>
                                    {`${formatAmount(computeStandaloneItemsTotal(data.standaloneItemsGroups))}${t("Quotation.LinkPage.JPY")}`}
                                </Text>
                            </View>
                        </View>
                    )}

                    {/* Total Row */}
                    <View style={styles.totalRow}>
                        <Text style={styles.totalRowLabel}>
                            <Text style={styles.totalRowLabel1}>
                                {t("Quotation.LinkPage.Table.Total")}
                            </Text>{" "}
                            <Text style={styles.totalRowLabel2}>
                                {t("Quotation.LinkPage.Table.TaxInclude")}
                            </Text>
                        </Text>
                        <Text style={styles.totalRowValue}>{`${formatAmount(computeQuotationTotalAmount(data))}${t("Quotation.LinkPage.JPY")}`}</Text>
                    </View>
                </View>
                {/* Quotation notes section */}
                {data?.notes && (
                    <View style={styles.notesSection}>
                        <Text style={styles.quotationNotesTitle}>{t("Quotation.LinkPage.QuotationNotes")}</Text>
                        <Text style={styles.noteContent}>
                            {data?.notes}
                        </Text>
                    </View>
                )}

                {/* Transfer Notes Section */}
                {/* {
                    data?.bookings?.some(booking => booking?.message !== null) && (
                        <View style={styles.notesSection}>
                            <Text style={styles.notesTitle}>{t("Quotation.LinkPage.TransferNotes")}</Text>
                            {data?.bookings?.map((item, index) => (
                                item?.message !== null && (
                                    <View key={index} style={styles.note}>
                                        <Text style={styles.noteTitle}>
                                            {(index + 1) + ". " + t(`BookingType.${getBookingType(item?.bookingType, item?.isAirportTransfer)}`)} ({item?.bookingItems.find((item) => item.type === "vehicle")?.serviceDate})
                                        </Text>
                                        {

                                            item?.message.map((m, pIndex) => (
                                                <Text key={pIndex} style={styles.noteContent}>
                                                    {m.note}
                                                </Text>
                                            ))

                                        }
                                    </View>
                                )
                            )
                            )}
                        </View>
                    )
                } */}
            </Page>
        </Document >
    )
}

export default QuotationDocument

